import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material';
import {
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip as TooltipRecharts,
} from 'recharts';

export const POSITION_LABELS: Record<number, string> = {
  0: 'Front',
  1: 'Rear',
  2: 'Left',
  3: 'Right',
};

// Blues palette matching the mockup
const POSITION_COLORS: Record<number, string> = {
  0: '#2196f3', // Front — medium blue
  1: '#0d47a1', // Rear — dark navy
  2: '#64b5f6', // Left — light blue
  3: '#90a4ae', // Right — blue-grey
};

interface PositionTableProps {
  counts: Record<number, number>;
  total: number;
}

interface PositionRow {
  camId: number;
  label: string;
  count: number;
  percent: string;
  color: string;
}

export const PositionTable = ({counts, total}: PositionTableProps) => {
  const theme = useTheme();

  const rows: PositionRow[] = Object.entries(POSITION_LABELS)
    .map(([camIdStr, label]) => {
      const camId = Number(camIdStr);
      const count = counts[camId] ?? 0;
      return {
        camId,
        label,
        count,
        percent: total > 0 ? ((count / total) * 100).toFixed(1) : '0.0',
        color: POSITION_COLORS[camId],
      };
    })
    .filter((r) => r.count > 0)
    .sort((a, b) => b.count - a.count);

  const pieData = rows.map((r) => ({
    name: r.label,
    value: r.count,
    color: r.color,
  }));

  const renderCustomizedLabel = (options: any) => {
    if (options.percent < 0.05) {
      return null;
    }
    const radius =
      options.innerRadius + (options.outerRadius - options.innerRadius) * 0.5;
    const x =
      options.cx + radius * Math.cos((-options.midAngle * Math.PI) / 180);
    const y =
      options.cy + radius * Math.sin((-options.midAngle * Math.PI) / 180);
    return (
      <text
        x={x}
        y={y}
        fill="white"
        textAnchor={x > options.cx ? 'start' : 'end'}
        dominantBaseline="central"
        fontSize={12}
      >
        {`${(options.percent * 100).toFixed(1)}%`}
      </text>
    );
  };

  const CustomTooltip = (options: any) => {
    if (options.active && options.payload?.length) {
      const item = options.payload[0].payload;
      return (
        <div
          style={{
            backgroundColor: theme.palette.background.default,
            color: theme.palette.text.secondary,
            borderColor: theme.palette.text.secondary,
            borderStyle: 'solid',
            borderWidth: '1px',
            padding: '4px 8px',
          }}
        >
          <strong>{item.name}</strong>
          <br />
          <span>{`Count: ${item.value}`}</span>
          <br />
          <span>{`${total > 0 ? ((item.value / total) * 100).toFixed(1) : '0.0'}%`}</span>
        </div>
      );
    }
    return null;
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      height="100%"
      overflow="hidden"
      p={1}
    >
      <Typography variant="subtitle1" fontWeight="bold" textAlign="center" mb={1}>
        Distribution by Position
      </Typography>

      {/* Pie Chart */}
      <Box flex="1" minHeight={0}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={pieData}
              cx="50%"
              cy="50%"
              labelLine={false}
              label={renderCustomizedLabel}
              outerRadius="75%"
              dataKey="value"
            >
              {pieData.map((entry) => (
                <Cell key={entry.name} fill={entry.color} />
              ))}
            </Pie>
            <TooltipRecharts cursor={false} content={<CustomTooltip />} />
            <Legend />
          </PieChart>
        </ResponsiveContainer>
      </Box>

      {/* Summary Table */}
      <TableContainer sx={{overflow: 'auto', maxHeight: 200}}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{fontWeight: 'bold', textTransform: 'uppercase'}}>
                Position
              </TableCell>
              <TableCell
                align="right"
                sx={{fontWeight: 'bold', textTransform: 'uppercase'}}
              >
                Count
              </TableCell>
              <TableCell
                align="right"
                sx={{fontWeight: 'bold', textTransform: 'uppercase'}}
              >
                %
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.camId}>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: '2px',
                        bgcolor: row.color,
                        flexShrink: 0,
                      }}
                    />
                    {row.label}
                  </Box>
                </TableCell>
                <TableCell align="right">{row.count}</TableCell>
                <TableCell align="right">{row.percent}%</TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={3} align="center">
                  No detections
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};
